import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getPermissions } from '../../lib/accessControl';
import { fetchPostShipmentLists } from '../../store/slices/dealDropdownOptions';
import { fetchCertificatesOfOrigin, fetchUploadedDocuments, fetchDocumentRevisionList } from '../../store/slices/dealUploadedDocuments';
import { formatDateFields } from '../../utils/dateUtils';
import { abortController } from '../../utils/abortController';
import useSessionExpire from '../../hooks/useSessionExpire';
import useUser from '../../hooks/useUser';
import Documentation from './Documentation';
import ShipmentDetails from './ShipmentDetails';
import Modal from 'react-bootstrap/Modal';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddBoxIcon from '@mui/icons-material/AddBox';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import dayjs from 'dayjs';
import axios from 'axios';
import { Tooltip } from '@mui/material';
import useTab from '../../hooks/useTab';
import { roles } from '../../utils/roles';

const PostShipment = forwardRef(({ isWritable = false, isLoading = false, isListDataLoading = false, isFoodsOrPolymersDeal = false, isFoodsPolymersOrSBLCDeal = false, isSBLCDeal = false, isUpdated = false, selectedBusinessUnit = null, selectedTranche = null, selectedDeal = null, setIsUpdated = () => { }, setIsDocumentToBeGenerated = () => { }, setSelectedTranche = () => { }, setShowConfirmationDialog = () => { }, setIsLoading = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, logOut = () => { } }, ref) => {

  const {
    selectedPFI = null,
  } = useTab();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* AUTHORIZATION VARIABLES */
  const { isWritable: isDocumentWritable = false } = getPermissions(currentUser?.role, null, "documents");
  const documentPermissions = getPermissions(currentUser?.role, null, "approvableDocuments");
  const documentRequestPermissions = getPermissions(currentUser?.role, null, "documentRequestPermissions");
  const disableDeletion = [roles[3], roles[6], roles[7], roles[8], roles[12], roles[15], roles[18]].includes(currentUser?.role);

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* LIST VARIABLES */
  const postShipmentListData = useSelector((state) => state.dealDropdownOptions.postShipment);
  const [isPostShipmentListDataLoading, setIsPostShipmentListDataLoading] = useState(false);
  const [togglePostShipmentListData, setTogglePostShipmentListData] = useState(false);
  const [tranches, setTranches] = useState([]);
  const [banks, setBanks] = useState([]);

  /* SELECTED LIST VARIABLES */
  const [selectionType, setSelectionType] = useState(null);
  const [stagedTranche, setStagedTranche] = useState(null);

  /* AUTO-POPULATE DATA VARIABLES */
  const [isDealsListForCopyLoading, setIsDealsListForCopyLoading] = useState(false);
  const [autopopulatedDealsList, setAutopopulatedDealsList] = useState([]);
  const [selectedDealForAutopopulate, setSelectedDealForAutopopulate] = useState(selectedDeal);

  const [isPFIsListForCopyLoading, setIsPFIsListForCopyLoading] = useState(false);
  const [pfisForCopy, setPFIsForCopy] = useState([]);
  const [selectedPFIToAutoPopulate, setSelectedPFIToAutoPopulate] = useState(selectedPFI);

  const [isTranchesListForCopyLoading, setIsTranchesListForCopyLoading] = useState(false);
  const [tranchesForCopy, setTranchesForCopy] = useState([]);
  const [selectedTrancheForAutoPopulate, setSelectedTrancheForAutoPopulate] = useState(null);
  const [stagedTrancheToCopy, setStagedTrancheToCopy] = useState(null);
  const [isCurrenTrancheANewTranche, setIsCurrenTrancheANewTranche] = useState(false);

  /* LINKED DEAL VARIABLES */
  const [isLinkedThroughRoute, setIsLinkedThroughRoute] = useState(false);
  const [linkedPFI, setLinkedPFI] = useState(null);
  const [linkedTranche, setLinkedTranche] = useState(null);
  const [linkedPFIProducts, setLinkedPFIProducts] = useState([]);
  const [currentPFIProducts, setCurrentPFIProducts] = useState([]);
  const [linkedPFIProductGrades, setLinkedPFIProductGrades] = useState([]);
  const [currentPFIProductGrades, setCurrentPFIProductGrades] = useState([]);
  const [linkedTrancheCOOs, setLinkedTrancheCOOs] = useState([]);

  /* PERIOD LOCK VARIABLES */
  const [lockedFields, setLockedFields] = useState([]);
  const [periodLockDate, setPeriodLockDate] = useState(structuredClone(null));
  const [isCurrentTrancheUnlocked, setIsCurrentTrancheUnlocked] = useState(false);

  /* FORM VARIABLES */
  const [expandedFormSections, setExpandedFormSections] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
  const formRef = useRef(null);

  /* Post Shipment Variables */
  const postShipmentDataSchema = {
    Tranche_Id: null,
    Tranche_Name: "",
    Shipping_Line: null,
    Vessel_Name: "",
    Voyage_No: "",
    Transport_Reference_Value: "Bill of Lading No.",
    Split_Bill_of_Lading_No: null,
    Truck_Receipt_No: null,
    Airway_Bill_No: null,
    Transport_Reference: null,
    Master_Bill_of_Lading_No: "",
    Shipment_Date: null,
    customer_payment_maturity_date: null,
    supplier_payment_maturity_date: null,
    Demurrage_Free_Days_at_Disport: null,
    Estimated_Arrival_to_POD: null,
    NN_Docs_Supplier: null,
    NN_Docs_Buyer: null,
    Documents_Status_to_Customer: "",
    Documents_Status_to_Supplier: "",
    Delivery_Date: null,
    Commercial_Invoice_No: "",
    Commercial_Invoice_Date: null,
    Gross_Weight_KGs: null,
    Net_Weight_KGs: null,
    Insurance_Cover_No: "",
    SI_Date: null,
    Insurance_Company_Details: "",
    Shipper: "",
    Notify_Bank: "",
    Notify_Customer: "",
    Final_Destination: "",
    Partial_Shipment: "",
    Transhipment: "",
    Port_Of_Loading_Id: null,
    Port_Of_Discharge_Id: null,
    Consignee: "",
    Shipment_Advice_Days: null,
    Certificates_of_Origin: [],
    tranche_products_selected: [],
    tranche_product_grades_selected: [],
    AWB_from_Supplier: "",
    Advance_Payment_Date: null,
    Supplier_Document_Receiving_Date: null,
    Discounting_Bank_Id: null,
    Export_Docs_Received_Date: null,
    Export_Docs_Submission_Date: null,
    Due_Date: null,
    Disc_Acceptance: "",
    Special_Requirement: "",
    Special_Requirement_SI: "",
    Special_Requirement_CI: "",
    Special_Requirement_SA: "",
    Special_Requirement_PL: "",
    Special_Requirement_BE: "",
    Advance_Payment_Receive_Date: null,
    Final_Payment_Receive_Date: null,
    AWB_To_Customer: null,
    Final_Payment_Date: null,
    Dispatch_Date: null,
    acceptance_date_to_customer: null,
    acceptance_date_from_supplier: null,
    Post_Pre_Discounting: null,
    Supplier_Transaction_Status: "",
    Supplier_Status: null,
    Supplier_Discrepancy_Reason: "",
    Supplier_Discrepancy_Acceptance_Swift: null,
    Customer_Transaction_Status: "",
    Customer_Status: null,
    Customer_Discrepancy_Reason: "",
    Customer_Discrepancy_Acceptance_Swift: null,
    Target_Customer_PFI_Issuance_Date: null,
    Target_Supplier_LC_Issuance_Date: null,
    Target_Customer_NN_Documents: null,
    Target_Supplier_NN_Documents: null,
    Target_Supplier_Original_Documents: null,
    Target_Customer_Original_Documents: null,
    Customer_LC_Deadline: null,
    Advance_Amount: null,
    Discount: null,
    Freight: null,
    FOB_Value: null,
    Label: "",
    Value: null,
    Transaction_Type: null
  };
  const sblcTrancheProductGradeDataSchema = {
    Deal_PFI_Id: null,
    Tranche_Id: null,
    SBLC_Id: null,
    PFIs: [],
    PFI_Id: []
  };
  const [isExistingPostShipment, setIsExistingPostShipment] = useState(false);
  const [isPostShipmentFormUpdated, setIsPostShipmentFormUpdated] = useState(false);
  const [isSBLCFormUpdated, setIsSBLCFormUpdated] = useState(false);
  const [isPostShipmentFormSavedOrDeleted, setIsPostShipmentFormSavedOrDeleted] = useState(false);
  const [postShipmentInitialFormData, setPostShipmentInitialFormData] = useState(structuredClone(postShipmentDataSchema));
  const [postShipmentFormData, setPostShipmentFormData] = useState(structuredClone(postShipmentDataSchema));
  const [sblcInitialFormData, setSBLCInitialFormData] = useState(structuredClone(sblcTrancheProductGradeDataSchema));
  const [sblcFormData, setSBLCFormData] = useState(structuredClone(sblcTrancheProductGradeDataSchema));

  // LINKED DEAL FORM DATA
  const linkedDealPostShipmentFormDataSchema = {
    tranche_products_selected: [],
    tranche_product_grades_selected: [],
    linked_tranche_id: null,
  }
  const [linkedDealPostShipmentFormData, setLinkedDealPostShipmentFormData] = useState(structuredClone(linkedDealPostShipmentFormDataSchema));
  const [commmercialInvoiceNo, setCommmercialInvoiceNo] = useState(null);

  /* DOCUMENT VARIABLES */
  const documentData = useSelector((state) => state.dealUploadedDocuments);
  const [areDocumentsLoading, setAreDocumentsLoading] = useState(false);
  const [toggleDocumentList, setToggleDocumentList] = useState(false);

  /* FILE VARIABLES */
  const [areFilesLoading, setAreFilesLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [requestedFiles, setRequestedFiles] = useState([]);
  const [toggleFileList, setToggleFileList] = useState(false);

  /* MODAL VARIABLES */
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /* REFERENCE VARIBLES */
  const shipmentDetailsRef = useRef(null);
  const documentationRef = useRef(null);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (selectedDeal) {
      setSelectedDealForAutopopulate(selectedDeal);
      setSelectedPFIToAutoPopulate(selectedPFI)
      setIsExistingPostShipment(false);
      setIsLinkedThroughRoute(Boolean(Number(selectedDeal?.is_route_linked_deal)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal?.id, selectedPFI?.id]);

  // Fetch current PFI products when PFI changes
  useEffect(() => {
    const fetchCurrentPFIProducts = async () => {
      if (!selectedPFI?.id || isFoodsOrPolymersDeal) {
        setCurrentPFIProducts([]);
        return;
      }

      try {
        const response = await axios.post('/api/GetDealProducts', {
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: selectedPFI.id
        });

        if (response.status === 200 && response.data?.deal_products) {
          setCurrentPFIProducts(response.data.deal_products);
        } else {
          setCurrentPFIProducts([]);
        }
      } catch (error) {
        console.error('Error fetching current PFI products:', error);
        setCurrentPFIProducts([]);
      }
    };

    fetchCurrentPFIProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPFI?.id, selectedBusinessUnit]);

  useEffect(() => {
    const fetchCurrentPFIProductGrades = async () => {
      if (!selectedPFI?.id || !isFoodsOrPolymersDeal) {
        setCurrentPFIProductGrades([]);
        return;
      }

      try {
        const response = await axios.post('/api/GetDealProductGrades', {
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: selectedPFI.id
        });

        if (response.status === 200 && response.data?.deal_product_grades) {
          setCurrentPFIProductGrades(response.data.deal_product_grades);
        } else {
          setCurrentPFIProductGrades([]);
        }
      } catch (error) {
        console.error('Error fetching current PFI product grades:', error);
        setCurrentPFIProductGrades([]);
      }
    };

    fetchCurrentPFIProductGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPFI?.id, selectedBusinessUnit, isFoodsOrPolymersDeal]);

  useEffect(() => {
    const fetchLinkedPFI = async () => {

      if (!isLinkedThroughRoute || !selectedPFI?.id) {
        setLinkedPFI(null);
        return;
      }

      try {
        const response = await axios.post('/api/GetLinkedPFI', {
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: selectedPFI?.id
        });

        if (response.status === 200 && response.data) {
          setLinkedPFI(response.data);
          fetchFileData(response.data, null);
        } else {
          setLinkedPFI(null);
        }
      } catch (error) {
        console.error('Error fetching linked PFI:', error);
        setLinkedPFI(null);
      }
    };
    // setLinkedPFI({id:882, value:"PFI - Linked Deal Example"});

    fetchLinkedPFI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPFI?.id, isLinkedThroughRoute, selectedBusinessUnit]);
  useEffect(() => {
    const fetchLinkedPFIProductGrades = async () => {
      if (!linkedPFI?.id || !isFoodsOrPolymersDeal) {
        setLinkedPFIProductGrades([]);
        return;
      }

      try {
        const response = await axios.post('/api/GetDealProductGrades', {
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: linkedPFI.id
        });

        if (response.status === 200 && response.data?.deal_product_grades) {
          setLinkedPFIProductGrades(response.data.deal_product_grades);
        } else {
          setLinkedPFIProductGrades([]);
        }
      } catch (error) {
        console.error('Error fetching linked PFI product grades:', error);
        setLinkedPFIProductGrades([]);
      }
    };

    fetchLinkedPFIProductGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPFI?.id, selectedBusinessUnit, isFoodsOrPolymersDeal]);

  // Fetch linked PFI products when linked PFI changes
  useEffect(() => {
    const fetchLinkedPFIProducts = async () => {
      if (!linkedPFI?.id) {
        setLinkedPFIProducts([]);
        return;
      }

      try {
        const response = await axios.post('/api/GetDealProducts', {
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: linkedPFI.id
        });

        if (response.status === 200 && response.data?.deal_products) {
          setLinkedPFIProducts(response.data.deal_products);
        } else {
          setLinkedPFIProducts([]);
        }
      } catch (error) {
        console.error('Error fetching linked PFI products:', error);
        setLinkedPFIProducts([]);
      }
    };

    fetchLinkedPFIProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPFI?.id, selectedBusinessUnit]);

  // Fetch current tranche Certificates of Origin when tranche changes
  useEffect(() => {
    const fetchCurrentTrancheCOOs = async () => {
      if (!selectedTranche?.id) {
        return;
      }

      try {
        const response = await axios.post('/api/GetCertificatesOfOrigin', {
          Business_Unit_Id: selectedBusinessUnit,
          Tranche_Id: selectedTranche.id
        });

        if (response.status === 200 && Array.isArray(response.data)) {
        } else {
        }
      } catch (error) {
        console.error('Error fetching current tranche COOs:', error);
      }
    };

    fetchCurrentTrancheCOOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTranche?.id, selectedBusinessUnit]);

  // Fetch linked tranche Certificates of Origin when linked tranche changes
  useEffect(() => {
    const fetchLinkedTrancheCOOs = async () => {
      if (!linkedTranche?.id || !isLinkedThroughRoute) {
        setLinkedTrancheCOOs([]);
        return;
      }

      try {
        const response = await axios.post('/api/GetCertificatesOfOrigin', {
          Business_Unit_Id: selectedBusinessUnit,
          Tranche_Id: linkedTranche.id
        });

        if (response.status === 200 && Array.isArray(response.data)) {
          setLinkedTrancheCOOs(response.data);
        } else {
          setLinkedTrancheCOOs([]);
        }
      } catch (error) {
        console.error('Error fetching linked tranche COOs:', error);
        setLinkedTrancheCOOs([]);
      }
    };

    fetchLinkedTrancheCOOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedTranche?.id, isLinkedThroughRoute, selectedBusinessUnit]);

  const fetchLinkedTranche = async () => {
    // Only fetch if we have a linked deal and a current tranche with a name
    if (!isLinkedThroughRoute || !linkedPFI?.id || !selectedTranche?.id) {
      return;
    }

    try {
      const response = await axios.post('/api/GetLinkedTranche', {
        Business_Unit_Id: selectedBusinessUnit,
        Tranche_Id: selectedTranche?.id,
      });

      if (response.status === 200 && response.data) {
        setLinkedTranche(response.data[0] || null);
        fetchFileData(linkedPFI, response.data[0]);
      } else {
        setLinkedTranche(null);
      }
    } catch (error) {
      console.error('Error fetching linked tranche:', error);
      setLinkedTranche(null);
    }
  };

  useEffect(() => {

    fetchLinkedTranche();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTranche?.id,
    isLinkedThroughRoute,
    linkedPFI?.id,
    selectedBusinessUnit,
    selectedPFI?.id
  ]);

  useEffect(() => {
    const isCurrentTrancheUnlocked = async () => {
      axios({
        method: "post",
        url: "/api/IsCurrentTrancheUnlocked",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
          tranche_id: selectedTranche?.id,
        },
        signal: abortController.signal
      })
        .then((response) => {
          setIsLoading(false);
          const { status, data } = response;

          if (status === 200) {
            setIsCurrentTrancheUnlocked(data?.unlocked_today);
          }
        })
        .catch((error) => {
          console.log("Period Lock Field Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }

    if (selectedTranche?.period_lock === 0) {
      isCurrentTrancheUnlocked();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTranche]);

  const fetchPriodLockDate = useCallback(async () => {
    const abortController = new AbortController();
    try {
      const response = await axios.post("/api/GetPeriodLockDate", {
        pathname,
        Business_Unit_Id: selectedBusinessUnit,
      }, { signal: abortController.signal });

      const { status, data } = response;
      if (status === 200) {
        setPeriodLockDate(data?.period_lock_date);
      }
    } catch (error) {
      // Handle errors...
    }
  }, [pathname, selectedBusinessUnit]);


  useEffect(() => {
    const fetchPriodLockFields = async () => {
      axios({
        method: "post",
        url: "/api/GetPeriodLockFields",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
        },
        signal: abortController.signal
      })
        .then((response) => {
          setIsLoading(false);
          const { status, data } = response;

          if (status === 200) {
            setLockedFields(data?.data);
          }
        })
        .catch((error) => {
          console.log("Period Lock Field Api: ", error);

        })
        .catch((error) => {
          console.log("Period Lock Field Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }

    setLockedFields([]);
    fetchPriodLockFields();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const fetchDealsList = async () => {
      setIsDealsListForCopyLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      try {
        const response = await axios({
          method: "post",
          url: "/api/GetDealListForAutoPopulate",
          data: {
            pathname,
            Business_Unit_Id: selectedBusinessUnit
          },
          signal: abortController.signal,
        });

        setIsDealsListForCopyLoading(false);
        const { status, data } = response;

        if (status === 200) {
          setAutopopulatedDealsList(data?.deals || []);
        } else {
          setAlertMessage("An error occurred while fetching deals.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Get Deals List API: ", error);
        setIsDealsListForCopyLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching deals."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      }
    };

    if (selectedBusinessUnit && isWritable) {
      fetchDealsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessUnit, isWritable]);

  useEffect(() => {
    const fetchPFIsForCopy = async () => {
      if (!selectedDealForAutopopulate) {
        setPFIsForCopy([]);
        return;
      }
      setIsPFIsListForCopyLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");


      try {
        const response = await axios({
          method: "post",
          url: "/api/GetDealPFIListForAutoPopulate",
          data: {
            pathname,
            Deal_Id: selectedDealForAutopopulate?.id,
            Business_Unit_Id: selectedBusinessUnit
          },
          signal: abortController.signal,
        });

        const { status, data } = response;
        setIsPFIsListForCopyLoading(false);
        if (status === 200) {
          setPFIsForCopy(data?.deal_pfi || []);
        } else {
          setAlertMessage("An error occurred while fetching PFIs for auto-populate.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        setIsPFIsListForCopyLoading(false);
        console.error("Get Deal PFI List API: ", error);
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;
        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching PFIs."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      }
    };

    if (selectedDealForAutopopulate?.id && isWritable) {
      fetchPFIsForCopy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealForAutopopulate?.id, isWritable]);


  useEffect(() => {
    const fetchTranchesForCopy = async () => {
      if (!selectedDealForAutopopulate?.id || !selectedPFIToAutoPopulate?.id) {
        setTranchesForCopy([]);
        return;
      }

      setIsTranchesListForCopyLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      try {
        const response = await axios({
          method: "post",
          url: "/api/GetTranchesListForAutoPopulate",
          data: {
            pathname,
            Business_Unit_Id: selectedBusinessUnit,
            Deal_PFI_Id: selectedPFIToAutoPopulate?.id
          },
          signal: abortController.signal,
        });

        const { status, data } = response;
        setIsTranchesListForCopyLoading(false);
        if (status === 200) {
          const filteredTranches = data?.tranches?.filter(tranche => tranche.id !== selectedTranche?.id) || [];
          setTranchesForCopy(filteredTranches);
        } else {
          setAlertMessage("An error occurred while fetching tranches for auto-populate.");
          setAlertType("error");
          setShowAlert(true);
        }
      } catch (error) {
        setIsTranchesListForCopyLoading(false);
        console.error("Get Tranches List API: ", error);
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;
        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching tranches."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      }
    };

    if (selectedPFIToAutoPopulate?.id && isWritable) {
      fetchTranchesForCopy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPFIToAutoPopulate?.id, isWritable]);

  useEffect(() => {
    const fetchBanksList = async () => {
      if (!isSubmittingRef.current) setIsLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");
      setBanks([]);

      await axios({
        method: "post",
        url: "/api/GetBankList",
        data: {
          pathname,
          Business_Unit_Id: selectedBusinessUnit
        },
        signal: abortController.signal
      })
        .then((response) => {
          if (!isSubmittingRef.current) setIsLoading(false);
          const { status, data } = response;

          if (status === 200) {
            setBanks(data?.banks || []);
          } else {
            setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
            setAlertType("error");
            setShowAlert(true);
          }
        })
        .catch((error) => {
          console.log("Get Document Data Api: ", error);
          if (!isSubmittingRef.current) setIsLoading(false);
          if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
          const status = error?.response?.status;
          if (status === 403) {
            triggerSessionExpire();
          } else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access."
                : status === 429
                  ? "Request limit exceeded."
                  : "An error occurred while processing your request."
            );
            setAlertType("error");
            setShowAlert(true);
            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }

    if (selectedBusinessUnit) {
      fetchBanksList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessUnit, pathname]);


  useEffect(() => {
    const fetchLists = async () => {
      if (!isSubmittingRef.current) setIsLoading(true);
      setIsPostShipmentListDataLoading(true);

      try {
        const response = await axios.post("/api/GetTrancheList", {
          pathname,
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: selectedPFI?.id
        }, { signal: abortController.signal });

        if (response.status === 200) {
          if (!isSubmittingRef.current) setIsLoading(false);
          const refreshedTranches = response.data?.tranches || [];

          setTranches(refreshedTranches);
          if (selectedTranche?.id) {
            const matchedTranche = refreshedTranches.find(tranche => tranche.id === selectedTranche.id);
            if (matchedTranche) {
              setTranchesForCopy(refreshedTranches.filter(tranche => tranche.id !== matchedTranche?.id));
              setSelectedTranche(matchedTranche);
            }
          } else if (postShipmentFormData.Tranche_Name !== "") {
            const matchedTranche = refreshedTranches.find(tranche => tranche.value === postShipmentFormData?.Tranche_Name);
            if (matchedTranche) {
              setTranchesForCopy(refreshedTranches.filter(tranche => tranche.id !== matchedTranche.id));
              setSelectedTranche(matchedTranche);
            }
          } else {
            setTranchesForCopy(refreshedTranches.filter(tranche => tranche.id !== refreshedTranches?.[0]?.id));
            setSelectedTranche(refreshedTranches?.[0]);
          }
        }
        else {
          if (!isSubmittingRef.current) setIsLoading(false);
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }

        await dispatch(fetchPostShipmentLists({
          pathname,
          businessUnitId: selectedBusinessUnit,
          selectedPFI: selectedPFI?.id,
          isFoodsOrPolymersDeal,
          isFoodsPolymersOrSBLCDeal
        }))
          .unwrap()
          .catch(error => console.error("Failed to fetch post-shipment lists:", error))
          .finally(() => setIsPostShipmentListDataLoading(false));

      }
      catch (error) {
        console.log("Get Tranche List Api: ", error);
        handleApiError(error);
      }
    }

    if (selectedPFI?.id) {
      fetchLists();
      setPeriodLockDate(null);
      fetchPriodLockDate();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedDeal?.id, selectedPFI?.id, togglePostShipmentListData]);


  useEffect(() => {
    const getPostShipmentDealData = async (selectedTrancheId) => {
      try {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        // FIX: local variable seeded from state — used for the synchronous
        // decision below instead of the (stale) `isCurrenTrancheANewTranche`
        // state variable, whose setter update is not visible until the next
        // render. Previously, once this effect had set the flag to `true`
        // on an early run (e.g. while `tranches` was still empty), the very
        // next run of this same effect (triggered by `setSelectedTranche`
        // resolving) would still read the OLD `true` value from its closure
        // even after calling `setIsCurrenTrancheANewTranche(false)` a few
        // lines above — so `Tranche_Name` kept getting set back to "1"
        // instead of `selectedTranche?.value`, regardless of how many
        // tranches actually existed.
        let currentTrancheIsNew = isCurrenTrancheANewTranche;

        const promises = [
          axios.post(`${isFoodsPolymersOrSBLCDeal ? "/api/GetPostShipmentFoodsPolymersAndSBLC" : "/api/GetPostShipmentFertilizersAndPetroChemicals"}`, {
            Business_Unit_Id: selectedBusinessUnit,
            Deal_PFI_Id: selectedTrancheForAutoPopulate ? selectedPFIToAutoPopulate?.id : selectedPFI?.id,
            Tranche_Id: selectedTrancheId
          })
        ];

        if (isSBLCDeal) {
          promises.push(axios.post("/api/GetDealTrancheSBLCProductGrades", { Deal_PFI_Id: selectedPFI?.id, Tranche_Id: selectedTrancheId }));
        }

        const [rawPostShipmentData, rawSBLCTrancheProductGradeData] = await Promise.all(promises);
        const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;


        // Only treat this as "not a new tranche" when we're NOT copying/auto-populating
        // from another tranche. When `selectedTrancheForAutoPopulate` is set, the fetched
        // shipment_details belongs to the SOURCE tranche being copied from, so its
        // Tranche_Id naturally exists — that must not be read as "the current tranche
        // already exists", or it wipes the auto-generated name (e.g. "4") for the
        // genuinely-new tranche being created.
        if (
          !selectedTrancheForAutoPopulate &&
          (rawPostShipmentData?.data?.shipment_details?.Tranche_Id || rawPostShipmentData?.data?.shipment_details?.Tranche_ID)
        ) {
          currentTrancheIsNew = false;
          setIsCurrenTrancheANewTranche(false)
        }

        const combinedPostShipmentData = {
          ...(rawPostShipmentData?.data?.shipment_details || {}),
          ...(rawPostShipmentData?.data?.documentation || {})
        };
        const postShipmentData = isEmpty(combinedPostShipmentData) ? postShipmentDataSchema : combinedPostShipmentData;
        const finalPostShipmentData = selectedTrancheForAutoPopulate
          ? {
            ...postShipmentData,
            Commercial_Invoice_No: null,
            tranche_products_selected: [],
            tranche_product_grades_selected: []
          }
          : postShipmentData;

        const sblcTrancheProductGradeData = isEmpty(rawSBLCTrancheProductGradeData?.data) ? sblcTrancheProductGradeDataSchema : rawSBLCTrancheProductGradeData?.data;
        const formattedPostShipmentData = formatDateFields(finalPostShipmentData, [
          "Shipment_Date",
          "Estimated_Arrival_to_POD",
          "NN_Docs_Supplier",
          "NN_Docs_Buyer",
          "Delivery_Date",
          "Commercial_Invoice_Date",
          "SI_Date",
          "Advance_Payment_Date",
          "Advance_Payment_Receive_Date",
          "Final_Payment_Date",
          "Final_Payment_Receive_Date",
          "Export_Docs_Received_Date",
          "Export_Docs_Submission_Date",
          "Due_Date",
          "Advance_Receiving_Date",
          "Remaining_Balance_Receiving_Date",
          "Supplier_Document_Receiving_Date",
          "Dispatch_Date",
          "Target_Customer_PFI_Issuance_Date",
          "Target_Supplier_LC_Issuance_Date",
          "Target_Customer_NN_Documents",
          "Target_Supplier_NN_Documents",
          "Target_Supplier_Original_Documents",
          "Target_Customer_Original_Documents",
          "Customer_LC_Deadline",
          "Supplier_Due_Date",
          "UPAS_Bank_Due_Date",
          "TR_Settlement_Date"
        ]);
        const totalAmount = Number(formattedPostShipmentData?.Total_Amount) || 0;
        const freight = Number(formattedPostShipmentData?.Freight) || 0;
        const fobValue = Number(formattedPostShipmentData?.FOB_Value) || 0;

        if (totalAmount !== (freight + fobValue)) {
          formattedPostShipmentData.Freight = null;
          formattedPostShipmentData.FOB_Value = null;
        }

        // Use auto-generated tranche name for new tranches, otherwise use selected tranche value
        if (currentTrancheIsNew) {
          formattedPostShipmentData.Tranche_Name = postShipmentFormData?.Tranche_Name;
        } else {
          formattedPostShipmentData.Tranche_Name = selectedTranche?.value;
        }
        if (!formattedPostShipmentData.Transport_Reference_Value) {
          formattedPostShipmentData.Transport_Reference_Value = "Bill of Lading No.";
        }
        if (selectedTrancheForAutoPopulate) {

          formattedPostShipmentData.Tranche_Id = null;
          sblcTrancheProductGradeData.Tranche_Id = null;
          formattedPostShipmentData.Shipment_Date = null;
          formattedPostShipmentData.customer_payment_maturity_date = null;
          formattedPostShipmentData.supplier_payment_maturity_date = null;
          sblcTrancheProductGradeData.Shipment_Date = null;
          sblcTrancheProductGradeData.customer_payment_maturity_date = null;
          sblcTrancheProductGradeData.supplier_payment_maturity_date = null;



          // Remove Tranche_Id when copying
          const updatedPostShipmentInitial = { ...finalPostShipmentData, Tranche_Id: null, Shipment_Date: null, customer_payment_maturity_date: null, supplier_payment_maturity_date: null };
          const updatedSBLCInitial = { ...sblcTrancheProductGradeData, Tranche_Id: null, Shipment_Date: null, customer_payment_maturity_date: null, supplier_payment_maturity_date: null };

          setPostShipmentInitialFormData(updatedPostShipmentInitial);
          setSBLCInitialFormData(updatedSBLCInitial);
        } else {
          // Normal initialization with cloned data
          if (selectedDeal?.is_vantira_deal) {
            setCommmercialInvoiceNo(formattedPostShipmentData?.Commercial_Invoice_No || null);

          }
          setPostShipmentInitialFormData(structuredClone(formattedPostShipmentData));
          setSBLCInitialFormData(structuredClone(sblcTrancheProductGradeData));
        }

        // Always set form data to fresh clones
        setPostShipmentFormData(structuredClone(formattedPostShipmentData));
        setSBLCFormData(structuredClone(sblcTrancheProductGradeData));
      }
      catch (error) {
        console.log("Get Post-Shipment Deal Data Api: ", error);
        handleApiError(error);
      }
      finally {
        setIsLoading(false);
      }
    };

    if (tranches.length > 0 || tranchesForCopy.length > 0) {
      const selectedTrancheId = selectedTrancheForAutoPopulate?.id || selectedTranche?.id;
      getPostShipmentDealData(selectedTrancheId);
    }
    else {
      setIsCurrenTrancheANewTranche(true);
      setIsLoading(false);
      setPostShipmentInitialFormData({ ...postShipmentDataSchema, Tranche_Name: "1" });
      setPostShipmentFormData({ ...postShipmentDataSchema, Tranche_Name: "1" });
      setSBLCInitialFormData(structuredClone(sblcTrancheProductGradeDataSchema));
      setSBLCFormData(structuredClone(sblcTrancheProductGradeDataSchema));
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTranche, selectedTrancheForAutoPopulate]);


  useEffect(() => {
    const getLinkedTrancheData = async () => {
      // Only fetch if we have a linked deal, linked PFI, linked tranche, and NOT copying
      if (!isLinkedThroughRoute || !linkedPFI?.id || !linkedTranche?.id || selectedTrancheForAutoPopulate) {
        // Reset linked data if conditions aren't met
        setLinkedDealPostShipmentFormData(structuredClone(linkedDealPostShipmentFormDataSchema));
        return;
      }

      try {
        setIsLoading(true);

        const linkedResponse = await axios.post(
          `${isFoodsPolymersOrSBLCDeal ? "/api/GetPostShipmentFoodsPolymersAndSBLC" : "/api/GetPostShipmentFertilizersAndPetroChemicals"}`,
          {
            Business_Unit_Id: selectedBusinessUnit,
            Deal_PFI_Id: linkedPFI.id,
            Tranche_Id: linkedTranche.id
          }
        );

        const linkedDealPostShipmentData = linkedResponse?.data?.shipment_details || null;

        if (linkedDealPostShipmentData) {
          const linkedFormData = {
            tranche_products_selected: linkedDealPostShipmentData.tranche_products_selected || [],
            tranche_product_grades_selected: linkedDealPostShipmentData.tranche_product_grades_selected || [],
            linked_tranche_id: linkedTranche?.id || null
          };
          if (!selectedDeal?.is_vantira_deal) {
            setCommmercialInvoiceNo(linkedDealPostShipmentData?.Commercial_Invoice_No || null);
          }
          setLinkedDealPostShipmentFormData(structuredClone(linkedFormData));

        } else {
          setLinkedDealPostShipmentFormData(structuredClone(linkedDealPostShipmentFormDataSchema));
        }
      }
      catch (error) {
        console.log("Get Linked Tranche Post-Shipment Data Api failed:", error);
        // Don't call handleApiError here as it's not critical - just reset the data
        setLinkedDealPostShipmentFormData(structuredClone(linkedDealPostShipmentFormDataSchema));
      }
      finally {
        setIsLoading(false);
      }
    };

    getLinkedTrancheData();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    isLinkedThroughRoute,
    linkedPFI?.id,
    linkedTranche?.id,
    selectedTrancheForAutoPopulate,
    selectedBusinessUnit,
    isFoodsPolymersOrSBLCDeal
  ]);

  useEffect(() => {
    const fetchDocumentData = async () => {
      setAreDocumentsLoading(true);

      try {
        if (selectedTrancheForAutoPopulate || selectedTranche) {
          await dispatch(fetchCertificatesOfOrigin({
            selectedBusinessUnit,
            selectedTranche: selectedTrancheForAutoPopulate || selectedTranche
          })).unwrap();
        }

        await dispatch(fetchDocumentRevisionList({
          selectedBusinessUnit,
          selectedPFI: selectedPFI?.id,
          selectedTranche: selectedTrancheForAutoPopulate || selectedTranche
        })).unwrap();
      }
      catch (error) {
        handleApiError(error);
      }
      finally {
        setAreDocumentsLoading(false);
      }
    }

    fetchDocumentData();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTranche, selectedTrancheForAutoPopulate, toggleDocumentList]);

  const fetchFileData = async (passedPFI, passedTranche) => {
    setAreFilesLoading(true);

    try {
      // Fetch documents for selected tranche
      await dispatch(fetchUploadedDocuments({
        selectedPFI: passedPFI?.id,
        selectedTranche: passedTranche
      })).unwrap();

    }
    catch (error) {
      handleApiError(error);
    }
    finally {
      setAreFilesLoading(false);
    }
  }
  useEffect(() => {

    fetchFileData(selectedPFI, selectedTranche);
    if (linkedPFI?.id && linkedTranche?.id) {
      fetchFileData(linkedPFI, linkedTranche);
    } else if (linkedPFI?.id && !linkedTranche?.id) {
      fetchFileData(linkedPFI, null);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTranche, selectedPFI, toggleFileList]);

  useEffect(() => {
    setIsUpdated(isPostShipmentFormUpdated || isSBLCFormUpdated);

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isPostShipmentFormUpdated, isSBLCFormUpdated]);

  useEffect(() => {
    // Check if the 'formData' is empty
    const isEmpty = typeof postShipmentFormData === "object" && !postShipmentFormData?.Tranche_Id;
    if (!isExistingPostShipment) {

      if (!isEmpty && !isCurrenTrancheANewTranche) {
        setIsExistingPostShipment(true);
      }
      else {
        setIsExistingPostShipment(false);
      }

    }

    if (!isPostShipmentFormSavedOrDeleted) {
      const isPostShipmentFormUpdated = checkIfFormUpdated(postShipmentFormData, postShipmentInitialFormData);
      setIsPostShipmentFormUpdated(isPostShipmentFormUpdated);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [postShipmentFormData, selectedTrancheForAutoPopulate, isCurrenTrancheANewTranche]);

  // Add this useEffect to RESET files when switching deals/PFIs
  useEffect(() => {
    // Clear files when deal or PFI changes
    setFiles([]);
    setUploadedFiles([]);
  }, [selectedDeal?.id, selectedTranche?.id, selectedPFI?.id]);

  useEffect(() => {
    if (!documentData?.files) {
      return;
    }

    setFiles(prevFiles => {
      // Deduplicate by unique key
      const existingKeys = new Set(
        prevFiles.map(item => {
          const f = item.file || item;
          return `${f.dealPFIId}-${f.trancheId}-${f.name}`;
        })
      );

      const newFiles = documentData.files.filter(item => {
        const f = item.file || item;
        return !existingKeys.has(`${f.dealPFIId}-${f.trancheId}-${f.name}`);
      });

      const merged = newFiles.length === 0 ? prevFiles : [...prevFiles, ...newFiles];

      // Final filter: remove stale tranche files, but keep files with null trancheId
      return merged.filter(item => {
        const f = item.file || item;

        // If trancheId is null/undefined, keep only if dealPFIId matches current or linked PFI
        if (!f.trancheId) {
          return (
            f.dealPFIId === selectedPFI?.id ||
            (linkedPFI?.id && f.dealPFIId === linkedPFI?.id)
          );
        }

        // Otherwise only keep if it belongs to current or linked tranche
        return (
          f.trancheId === selectedTranche?.id ||
          (linkedTranche?.id && f.trancheId === linkedTranche?.id)
        );
      });
    });
  }, [
    documentData?.files,
    selectedPFI?.id,
    selectedTranche?.id,
    linkedPFI?.id,
    linkedTranche?.id,
  ]);

  const resetLockedFields = (formData, lockedFields = [], subTab, tabName = "Post-Shipment") => {

    const initialFormDataMap = {
      "Shipment Details": postShipmentInitialFormData,
      "SBLC Product Grade": sblcInitialFormData,
    };

    const initialFormData = initialFormDataMap[subTab] || formData;
    // 1. Initial data setup with safety checks
    const clonedInitialFormData = initialFormData
      ? structuredClone(initialFormData)
      : {};

    if (subTab === "SBLC Product Grade") {
      return clonedInitialFormData;
    }

    const relevantLockedFields = lockedFields.filter(field => field.tab === tabName);

    // Create a set of explicitly locked field names
    const lockedFieldNames = new Set(relevantLockedFields.map(field => field.field_name));

    // 3. Prepare updated form data
    const updatedFormData = structuredClone(formData || {});

    // 4. Handle both regular fields and product arrays
    const handleFieldReset = (target, source, field) => {
      const initialValue = source[field];

      if (initialValue !== undefined) {
        target[field] = initialValue;
      }
    };

    // Reset regular fields
    Object.keys(updatedFormData).forEach(key => {
      if (lockedFieldNames.has(key)) {
        handleFieldReset(updatedFormData, clonedInitialFormData, key);
      }
    });

    // Special handling for product/product grade arrays
    const productArrayKey = isFoodsOrPolymersDeal
      ? 'tranche_product_grades_selected'
      : 'tranche_products_selected';

    if (Array.isArray(postShipmentFormData[productArrayKey])) {
      updatedFormData[productArrayKey] = clonedInitialFormData[productArrayKey]
    }

    return updatedFormData;
  };

  const fetchRefreshedTranchesLists = async () => {
    try {
      const response = await axios.post("/api/GetTrancheList", {
        pathname,
        Business_Unit_Id: selectedBusinessUnit,
        Deal_PFI_Id: selectedPFI?.id
      }, { signal: abortController.signal });

      if (response.status === 200) {
        const tranches = response.data?.tranches || [];
        const currentTranche = tranches.find(t => t.id === selectedTranche?.id);
        setTranches(tranches);
        if (currentTranche) {
          return currentTranche
        }
      }
      else {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    }
    catch {
      console.log("fetching list  data api failed due to unknown reasons.");
    }
  }

  const matchProductsByIndex = (currentProducts = [], linkedProducts = [], currentPFIProductsList = [], linkedPFIProductsList = []) => {
    if (!Array.isArray(currentProducts) || currentProducts.length === 0) {
      return [];
    }

    return currentProducts.map((currentProduct, index) => {
      // Get the corresponding linked product at the same index
      const linkedProduct = linkedProducts?.[index];


      // Find the matching linked PFI product by index from currentPFIProducts
      let linkedDealProductId = null;

      if (currentProduct.Deal_Product_Id) {
        // Find the index of this Deal_Product_Id in currentPFIProductsList
        const currentProductIndex = currentPFIProductsList.findIndex(
          p => p.id === currentProduct.Deal_Product_Id.id
        );


        if (currentProductIndex !== -1 && linkedPFIProductsList[currentProductIndex]) {
          // Get the product at the same index from linkedPFIProductsList
          const linkedProduct = linkedPFIProductsList[currentProductIndex];
          linkedDealProductId = {
            id: linkedProduct.id,
            value: linkedProduct.value
          };

        } else {
          console.warn(`⚠ No matching linked product found at index ${currentProductIndex}`);
        }
      }

      // CASE 1: Product has Tranche_Product_Id (existing product in current tranche)
      if (currentProduct.Tranche_Product_Id) {

        // SUB-CASE 1A: Linked product exists at same index - UPDATE scenario
        if (linkedProduct && linkedProduct.Tranche_Product_Id) {
          const matched = {
            ...currentProduct,
            // Keep the linked tranche's IDs (for UPDATE)
            Tranche_Product_Id: linkedProduct.Tranche_Product_Id,
            Deal_Product_Id: linkedDealProductId || linkedProduct.Deal_Product_Id || currentProduct.Deal_Product_Id,

            // Override other fields from current product (the new data being saved)
            Shipped_Quantity: currentProduct.Shipped_Quantity,
            // Add any other fields that should be updated
          };

          console.log(`✓ Matched existing product at index ${index}:`, {
            currentTrancheProductId: currentProduct.Tranche_Product_Id,
            linkedTrancheProductId: linkedProduct.Tranche_Product_Id,
            linkedDealProductId: matched.Deal_Product_Id,
            shippedQuantity: matched.Shipped_Quantity
          });

          return matched;
        }

        // SUB-CASE 1B: No linked product at same index - INSERT as NEW
        else {
          const newProduct = {
            ...currentProduct,
            // Remove Tranche_Product_Id to force INSERT in linked tranche
            Tranche_Product_Id: undefined,
            // Use the matched linked Deal_Product_Id or keep current
            Deal_Product_Id: linkedDealProductId || currentProduct.Deal_Product_Id,
            Shipped_Quantity: currentProduct.Shipped_Quantity,
          };

          // Clean up undefined values
          delete newProduct.Tranche_Product_Id;

          console.log(`⚠ No match found for existing product at index ${index}, will INSERT as new:`, {
            currentTrancheProductId: currentProduct.Tranche_Product_Id,
            linkedDealProductId: newProduct.Deal_Product_Id,
            shippedQuantity: newProduct.Shipped_Quantity
          });

          return newProduct;
        }
      }

      // CASE 2: New product (no Tranche_Product_Id in current) - always INSERT
      else {
        const newProduct = {
          ...currentProduct,
          // No Tranche_Product_Id (will be inserted)
          Deal_Product_Id: linkedDealProductId || currentProduct.Deal_Product_Id,
          Shipped_Quantity: currentProduct.Shipped_Quantity,
        };

        console.log(`+ New product at index ${index} (no Tranche_Product_Id):`, {
          linkedDealProductId: newProduct.Deal_Product_Id,
          shippedQuantity: newProduct.Shipped_Quantity
        });

        return newProduct;
      }
    });
  };

  const matchProductGradesByIndex = (currentProductGrades = [], linkedProductGrades = [], currentPFIProductGradesList = [], linkedPFIProductGradesList = []) => {
    if (!Array.isArray(currentProductGrades) || currentProductGrades.length === 0) {
      return [];
    }

    return currentProductGrades.map((currentProductGrade, index) => {
      // Get the corresponding linked product grade at the same index
      const linkedProductGrade = linkedProductGrades?.[index];

      // Find the matching linked PFI product grade by index from currentPFIProductGrades
      let linkedDealProductGradeId = null;

      if (currentProductGrade.Deal_Product_Grade_Id) {
        // Find the index of this Deal_Product_Grade_Id in currentPFIProductGradesList
        const currentProductGradeIndex = currentPFIProductGradesList.findIndex(
          pg => pg.id === currentProductGrade.Deal_Product_Grade_Id.id
        );

        if (currentProductGradeIndex !== -1 && linkedPFIProductGradesList[currentProductGradeIndex]) {
          // Get the product grade at the same index from linkedPFIProductGradesList
          const linkedProductGrade = linkedPFIProductGradesList[currentProductGradeIndex];
          linkedDealProductGradeId = {
            id: linkedProductGrade.id,
            value: linkedProductGrade.value
          };

        } else {
          console.warn(`⚠ No matching linked product grade found at index ${currentProductGradeIndex}`);
        }
      }

      // CASE 1: Product grade has Tranche_Product_Grade_Id (existing product grade in current tranche)
      if (currentProductGrade.Tranche_Product_Grade_Id) {

        // SUB-CASE 1A: Linked product grade exists at same index - UPDATE scenario
        if (linkedProductGrade && linkedProductGrade.Tranche_Product_Grade_Id) {
          const matched = {
            ...currentProductGrade,
            // Keep the linked tranche's IDs (for UPDATE)
            Tranche_Product_Grade_Id: linkedProductGrade.Tranche_Product_Grade_Id,
            Deal_Product_Grade_Id: linkedDealProductGradeId || linkedProductGrade.Deal_Product_Grade_Id || currentProductGrade.Deal_Product_Grade_Id,

            // Override other fields from current product grade (the new data being saved)
            Shipped_Quantity: currentProductGrade.Shipped_Quantity,
          };

          return matched;
        }

        // SUB-CASE 1B: No linked product grade at same index - INSERT as NEW
        else {
          const newProductGrade = {
            ...currentProductGrade,
            // Remove Tranche_Product_Grade_Id to force INSERT in linked tranche
            Tranche_Product_Grade_Id: undefined,
            // Use the matched linked Deal_Product_Grade_Id or keep current
            Deal_Product_Grade_Id: linkedDealProductGradeId || currentProductGrade.Deal_Product_Grade_Id,
            Shipped_Quantity: currentProductGrade.Shipped_Quantity,
          };

          // Clean up undefined values
          delete newProductGrade.Tranche_Product_Grade_Id;

          return newProductGrade;
        }
      }

      // CASE 2: New product grade (no Tranche_Product_Grade_Id in current) - always INSERT
      else {
        const newProductGrade = {
          ...currentProductGrade,
          // No Tranche_Product_Grade_Id (will be inserted)
          Deal_Product_Grade_Id: linkedDealProductGradeId || currentProductGrade.Deal_Product_Grade_Id,
          Shipped_Quantity: currentProductGrade.Shipped_Quantity,
        };

        return newProductGrade;
      }
    });
  };

  const matchCOOsByIndex = (currentCOOs = [], linkedCOOs = []) => {
    if (!Array.isArray(currentCOOs) || currentCOOs.length === 0) {
      return [];
    }

    return currentCOOs.map((currentCOO, index) => {
      // Get the corresponding linked COO at the same index
      const linkedCOO = linkedCOOs?.[index];

      // CASE 1: COO has COO_ID (existing COO in current tranche)
      if (currentCOO.COO_ID) {

        // SUB-CASE 1A: Linked COO exists at same index - UPDATE scenario
        if (linkedCOO && linkedCOO.id) {
          const matched = {
            COO_ID: linkedCOO.id,  // Use linked tranche's COO ID for UPDATE
            COO_Title: currentCOO.COO_Title || "",
            COO_Content: currentCOO.COO_Content || ""
          };


          return matched;
        }

        // SUB-CASE 1B: No linked COO at same index - INSERT as NEW
        else {
          const newCOO = {
            COO_Title: currentCOO.COO_Title || "",
            COO_Content: currentCOO.COO_Content || ""
            // No COO_ID means it will be inserted as new
          };


          return newCOO;
        }
      }

      // CASE 2: New COO (no COO_ID in current) - always INSERT
      else {
        const newCOO = {
          COO_Title: currentCOO.COO_Title || "",
          COO_Content: currentCOO.COO_Content || ""
        };


        return newCOO;
      }
    });
  };

  // ============================================================================
  // Updated submission logic in useImperativeHandle
  // ============================================================================
  useImperativeHandle(ref, () => ({
    handleSubmit: async () => {
      isSubmittingRef.current = true;
      setIsLoading(true);

      try {
        // Check if formRef exists and is valid
        if (!formRef.current?.reportValidity()) {
          setIsLoading(false);
          return;
        }

        // Validate child components
        const validationResults = await Promise.all([
          shipmentDetailsRef.current?.isValid() ?? false,
          documentationRef.current?.isValid() ?? false
        ]);

        // Exit if any validation fails
        if (validationResults.includes(false)) {
          setIsLoading(false);
          return;
        }

        // Handle tranche lock check
        let isCurrentTrancheLocked = null;
        if (selectedTranche?.period_lock === 0) {
          const currentTranche = await fetchRefreshedTranchesLists();
          isCurrentTrancheLocked = currentTranche?.period_lock;
        }

        let formDataToUse = isSBLCDeal ? sblcFormData : postShipmentFormData;

        const transportRefValue = formDataToUse?.Transport_Reference_Value;
        const transportRefCleanup = {
          Split_Bill_of_Lading_No: transportRefValue === "Bill of Lading No." ? formDataToUse?.Split_Bill_of_Lading_No : null,
          Truck_Receipt_No: transportRefValue === "Truck Receipt No." ? formDataToUse?.Truck_Receipt_No : null,
          Airway_Bill_No: transportRefValue === "Airway Bill No." ? formDataToUse?.Airway_Bill_No : null,
          Transport_Reference: transportRefValue === "Transport Reference" ? formDataToUse?.Transport_Reference : null,
        };

        const currentDealformData = {
          ...formDataToUse,
          ...transportRefCleanup,
          Deal_PFI_Id: selectedPFI.id,
          is_vantira_deal: selectedDeal?.is_vantira_deal || false
        };
        // ========================================================================
        // STEP 1: Submit current tranche
        // ========================================================================
        const firstSubmitResult = await shipmentDetailsRef.current?.handleSubmit(
          undefined,
          isCurrentTrancheLocked,
          currentDealformData,
          selectedTranche?.id || null
        );

        // If first submission failed, stop here
        if (!firstSubmitResult) {
          setIsLoading(false);
          return;
        }

        // Store the original tranche ID (either existing or newly created)
        const originalTrancheId = firstSubmitResult;

        if (firstSubmitResult && selectedDeal?.is_vantira_deal) {
          if (
            isLinkedThroughRoute &&
            postShipmentFormData?.Commercial_Invoice_No !== commmercialInvoiceNo &&
            commmercialInvoiceNo
          ) {
            try {
              await axios({
                method: "post",
                url: "/api/SetTrancheCommercialInvoice",
                data: {
                  Business_Unit_Id: selectedBusinessUnit,
                  Tranche_Id: originalTrancheId,
                  Commercial_Invoice: postShipmentFormData?.Commercial_Invoice_No
                },
                signal: abortController.signal
              });
            } catch (ciError) {
              console.error('❌ Error updating Commercial Invoice Number:', ciError);
              // Don't fail the whole operation, just log it
            }
          }
        }

        // ========================================================================
        // STEP 2: Submit linked tranche if applicable
        // ========================================================================
        if (isLinkedThroughRoute && linkedPFI?.id) {
          try {

            // Determine which product array to use based on deal type
            const currentProductsKey = isFoodsOrPolymersDeal
              ? 'tranche_product_grades_selected'
              : 'tranche_products_selected';

            const linkedProductsKey = isFoodsOrPolymersDeal
              ? 'tranche_product_grades_selected'
              : 'tranche_products_selected';

            let matchedProducts;

            // Use different matching logic based on deal type
            if (isFoodsOrPolymersDeal) {
              // Match product GRADES by index for Foods/Polymers deals
              matchedProducts = matchProductGradesByIndex(
                formDataToUse[currentProductsKey] || [],
                linkedDealPostShipmentFormData[linkedProductsKey] || [],
                currentPFIProductGrades,
                linkedPFIProductGrades
              );
            } else {
              // Match PRODUCTS by index for other deals
              matchedProducts = matchProductsByIndex(
                formDataToUse[currentProductsKey] || [],
                linkedDealPostShipmentFormData[linkedProductsKey] || [],
                currentPFIProducts,
                linkedPFIProducts
              );
            }

            // Match Certificates of Origin by index
            const matchedCOOs = matchCOOsByIndex(
              formDataToUse.Certificates_of_Origin || [],
              linkedTrancheCOOs
            );

            // Create a copy of the form data with the linked PFI ID, matched products, and matched COOs
            const linkedFormData = {
              ...formDataToUse,
              ...transportRefCleanup,
              Deal_PFI_Id: linkedPFI.id,
              tranche_products_selected: isFoodsOrPolymersDeal ? [] : matchedProducts,
              tranche_product_grades_selected: isFoodsOrPolymersDeal ? matchedProducts : [],
              Certificates_of_Origin: matchedCOOs
            };
            // Wait a moment for state to settle
            await new Promise(resolve => setTimeout(resolve, 100));

            // Submit with linked PFI data
            const linkedSubmitResult = await shipmentDetailsRef.current?.handleSubmit(
              undefined,
              isCurrentTrancheLocked,
              linkedFormData,
              linkedTranche?.id || null
            );

            if (linkedSubmitResult) {

              // If linkedTranche?.id was null (newly created), link the tranches
              if (!linkedTranche?.id) {
                try {

                  const response = await axios.post("/api/SetLinkedTranche", {
                    Business_Unit_Id: selectedBusinessUnit,
                    Tranche_Id: originalTrancheId,
                    Linked_Tranche_Id: linkedSubmitResult
                  }, { signal: abortController.signal });

                  if (response.status === 200) {
                    setLinkedTranche(response.data);

                    setAlertMessage("Data saved successfully for both current and linked deals.");
                    setAlertType("success");
                    setShowAlert(true);
                  }
                  else {
                    setAlertMessage("Data saved but failed to link tranches. Please contact the administrator.");
                    setAlertType("warning");
                    setShowAlert(true);
                  }
                } catch (linkError) {
                  console.error('❌ Error linking tranches:', linkError);
                  setAlertMessage("Data saved but failed to link tranches. Please contact the administrator.");
                  setAlertType("warning");
                  setShowAlert(true);
                }
              } else {
                setAlertMessage("Data saved successfully for both current and linked deals.");
                setAlertType("success");
                setShowAlert(true);
              }
            } else {
              console.log("⚠ Linked tranche submission returned no result");
              setAlertMessage("Data saved successfully.");
              setAlertType("success");
              setShowAlert(true);
            }

          } catch (error) {
            console.error('❌ Error during linked PFI submission:', error);
            setAlertMessage("Data saved successfully.");
            setAlertType("success");
            setShowAlert(true);
          }
        } else {
          // No linked deal
          setAlertMessage("Data saved successfully.");
          setAlertType("success");
          setShowAlert(true);
        }

      } catch (error) {
        console.error("❌ Error in Add/Update Post-Shipment data:", error);
        setAlertMessage("An error occurred while saving data. Please try again.");
        setAlertType("error");
        setShowAlert(true);
      } finally {
        isSubmittingRef.current = false;
        setIsLoading(false);
      }
    }
  }));

  const checkIfFormUpdated = (formData, initialFormData, dateKeys = []) => {
    // Check non-date fields
    const isNonDateUpdated = Object.keys(initialFormData).some((key) => {
      const currentValue = formData[key];
      const initialValue = initialFormData[key];

      return JSON.stringify(currentValue) !== JSON.stringify(initialValue);
    });

    // Check date fields separately
    const isDateUpdated = dateKeys.some((dateKey) => {
      const currentDate = formData[dateKey] ? dayjs(formData[dateKey]) : formData[dateKey];
      const initialDate = initialFormData[dateKey] ? dayjs(initialFormData[dateKey]) : initialFormData[dateKey];

      return JSON.stringify(currentDate) !== JSON.stringify(initialDate);
    });

    return isNonDateUpdated || isDateUpdated;
  }

  const handleApiError = (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    switch (status) {
      case 400:
        setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        break;
      case 403:
        triggerSessionExpire();
        break;
      case 401:
        setAlertMessage("Unauthorized access. You do not have the required permissions to perform this action.");
        setAlertType("error");
        setShowAlert(true);
        break;
      case 429:
        setAlertMessage("Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        setTimeout(logOut, 3000);
        break;
      default:
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        break;
    }
  }

  const handleSelectTrancheWrapper = (selectionType, newValue) => {
    const isEmpty = Object.entries(postShipmentFormData)?.every(([key, value]) => value === "" || value === null || value === undefined) && uploadedFiles.length === 0;

    if (!isEmpty && !isPostShipmentFormSavedOrDeleted && (!isExistingPostShipment || isUpdated) && !isCurrenTrancheANewTranche) {
      setSelectionType(selectionType);

      if (newValue) {
        if (selectionType === "Copy") {
          setStagedTrancheToCopy(newValue);
        }
        else if (selectionType === "Select") {
          setStagedTranche(newValue);
        }

        setShowSelectModal(true);
      }
    }
    else {
      handleSelectTranche(selectionType, newValue);
    }
  }

  const handleSelectTranche = (selectionType, newValue) => {
    setShowSelectModal(false);

    if (selectionType === "Copy") {
      setSelectedTrancheForAutoPopulate(newValue);
    }
    else if (selectionType === "Select") {
      setSelectedTranche(newValue);
      setTranchesForCopy(tranches.filter(tranche => tranche.id !== newValue?.id));
      setSelectedTrancheForAutoPopulate(null);
    }

    setIsUpdated(false);
  }

  const handleConfirmTrancheSelection = () => {
    if (selectionType === "Copy") {
      handleSelectTranche("Copy", stagedTrancheToCopy);
    }
    else if (selectionType === "Select") {
      handleSelectTranche("Select", stagedTranche);
    }
  }

  const handleChange = (field, value, isDate = false) => {
    let newValue = value;

    if (isDate && newValue) {
      newValue = dayjs(newValue).format("MM/DD/YYYY");
    }

    if (["Gross_Weight_KGs", "Net_Weight_KGs"].includes(field)) {
      // Remove any non-numeric characters except the decimal point
      newValue = value.replace(/[^0-9.]/g, "");

      // Ensure the value has at most 3 decimal places
      if (newValue.includes(".")) {
        const [integerPart, decimalPart] = newValue.split(".");
        newValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
      }
    }

    setPostShipmentFormData((previousFormData) => ({
      ...previousFormData,
      [field]: typeof newValue === "string" ? newValue.trim() === "" ? null : newValue : newValue ? newValue : null
    }));

    setIsPostShipmentFormSavedOrDeleted(false);
  }

  const handleAddTrancheWrapper = () => {
    setIsCurrentTrancheUnlocked(false);
    setSelectedTranche(null);
    setLinkedTranche(null);
    setTranchesForCopy(tranches)
    setIsCurrenTrancheANewTranche(true)
    // Auto-generate tranche name for the new tranche
    const nextTrancheNumber = tranches.length + 1;
    setPostShipmentFormData((prevFormData) => ({
      ...prevFormData,
      Tranche_Name: nextTrancheNumber.toString()
    }));
    shipmentDetailsRef.current?.handleAddTrancheWrapper();
  }

  const handleExpandAll = () => {
    setExpandedFormSections([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
  }

  const handleCollapseAll = () => {
    setExpandedFormSections([]);
  }

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);
      setIsLoading(true);

      let isCurrentTrancheLocked = false;

      if (
        selectedTranche?.hasOwnProperty('period_lock') &&
        selectedTranche.period_lock !== 0
      ) {
        const currentTranche = await fetchRefreshedTranchesLists();
        isCurrentTrancheLocked = currentTranche?.period_lock;
      }

      if (isCurrentTrancheLocked) {
        setAlertMessage("This tranche is locked and cannot be deleted.");
        setAlertType("error");
        setShowAlert(true);
        setIsLoading(false);
        return;
      }

      // First deletion: Delete current tranche
      await shipmentDetailsRef.current?.handleDelete(selectedPFI, selectedTranche);

      // If linked tranche exists, delete it as well
      if (isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id) {
        // Second deletion: Delete linked tranche
        await shipmentDetailsRef.current?.handleDelete(linkedPFI, linkedTranche);
      }

      // Show success message
      setAlertMessage(
        isLinkedThroughRoute && linkedTranche?.id
          ? "Tranche deleted successfully for both selected and linked tranches."
          : "Tranche deleted successfully."
      );
      setAlertType("success");
      setShowAlert(true);

    } catch (error) {
      console.error("Error deleting tranche:", error);
      setAlertMessage("Failed to delete tranche data.");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowSelectModal(false);
    setShowDeleteModal(false);
    shipmentDetailsRef.current?.handleCloseModal();
    documentationRef.current?.handleCloseModal();
  }

  return (
    <Grid
      container
      spacing={"10px"}
      className="input-fields align-items-start autocomplete"
    >
      <div className="w-100 d-flex justify-content-between align-items-center mt-2 flex-wrap row-gap-3">
        <div className="d-flex align-items-center gap-2 form-section-button">
          <Button
            variant="outlined"
            size="small"
            startIcon={<UnfoldMoreIcon />}
            disabled={expandedFormSections.length === 5}
            onClick={handleExpandAll}
          >
            Expand All
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<UnfoldLessIcon />}
            disabled={expandedFormSections.length === 0}
            onClick={handleCollapseAll}
          >
            Collapse All
          </Button>
        </div>

        <div className="copy-tranche autocomplete flex-wrap">
          <VirtualizedAutocomplete
            isLoading={isDealsListForCopyLoading}
            isMultiple={false}
            isObject={true}
            isWritable={isWritable}
            filterOn="Select Deal to Auto Populate"
            options={autopopulatedDealsList}
            selectedOptions={selectedDealForAutopopulate}
            handleSelectChange={(filterOn, newValue) => {
              setSelectedDealForAutopopulate(newValue)
              setSelectedPFIToAutoPopulate(null)
              setTranchesForCopy(tranches?.filter(tranche => tranche.id !== selectedTranche?.id) || []);
            }}
          />
          <VirtualizedAutocomplete
            isLoading={isPFIsListForCopyLoading}
            isMultiple={false}
            isObject={true}
            isWritable={isWritable}
            filterOn="Select PFI to Auto Populate"
            options={pfisForCopy?.length > 0 ? pfisForCopy : []}
            selectedOptions={selectedPFIToAutoPopulate}
            handleSelectChange={(filterOn, newValue) => {
              setSelectedTrancheForAutoPopulate(null)
              setSelectedPFIToAutoPopulate(newValue)
            }
            }
          />
          <VirtualizedAutocomplete
            isLoading={isTranchesListForCopyLoading}
            isMultiple={false}
            isObject={true}
            isWritable={isWritable}
            filterOn="Select Tranche to Auto Populate"
            options={tranchesForCopy}
            selectedOptions={selectedTrancheForAutoPopulate}
            handleSelectChange={(filterOn, newValue) => handleSelectTrancheWrapper("Copy", newValue)}
          />

        </div>
      </div>

      <form
        ref={formRef}
        className="w-100 position-relative"
        onSubmit={(event) => event.preventDefault()}
      >
        <Grid size={{ xs: 3 }} className="m-auto mt-3">
          <Tooltip title="Tranche name will be fetched automatically">
            <span>
              <TextField
                label="Tranche Name"
                id="Tranche_Name"
                value={postShipmentFormData?.Tranche_Name || ""}
                disabled
                fullWidth
                onChange={(event) => handleChange("Tranche_Name", event.target.value)}
                slotProps={{
                  input: {
                    readOnly: true
                  }
                }}
              />
            </span>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 3.5 }} className="position-absolute d-flex justify-content-center align-items-center gap-2" style={{ right: '12px', top: '15px' }}>
          <VirtualizedAutocomplete
            isLoading={isListDataLoading}
            isMultiple={false}
            isObject={true}
            isWritable={true}
            filterOn="Select Tranche Name"
            options={tranches}
            selectedOptions={selectedTranche}
            handleSelectChange={(filterOn, newValue) => {
              setSelectedDealForAutopopulate(selectedDeal)
              setSelectedPFIToAutoPopulate(selectedPFI)
              handleSelectTrancheWrapper("Select", newValue)
            }
            }
          />
          <IconButton
            color="primary"
            disabled={!isWritable}
            onClick={handleAddTrancheWrapper}
          >
            <AddBoxIcon
              fontSize="large"
              sx={{ color: '#0C969C' }}
            />
          </IconButton>
        </Grid>
      </form>

      <Grid size={{ xs: 12 }} className="mt-4">
        <ShipmentDetails
          ref={shipmentDetailsRef}
          isWritable={isWritable}
          isListDataLoading={isPostShipmentListDataLoading}
          isDocumentWritable={isDocumentWritable}
          isExistingShipmentDetails={isExistingPostShipment}
          isFormSavedOrDeleted={isPostShipmentFormSavedOrDeleted}
          isFoodsOrPolymersDeal={isFoodsOrPolymersDeal}
          isFoodsPolymersOrSBLCDeal={isFoodsPolymersOrSBLCDeal}
          isSBLCDeal={isSBLCDeal}
          isUpdated={isUpdated}
          selectedBusinessUnit={selectedBusinessUnit}
          selectedDeal={selectedDeal}
          selectedPFI={selectedPFI}
          selectedTranche={selectedTranche}
          selectedTrancheForAutoPopulate={selectedTrancheForAutoPopulate}
          listData={{ ...postShipmentListData, tranches }}
          formData={postShipmentFormData}
          setLockedFields={setLockedFields}
          lockedFields={lockedFields}
          periodLockDate={periodLockDate}
          isCurrentTrancheUnlocked={isCurrentTrancheUnlocked}
          resetLockedFields={resetLockedFields}
          fetchRefreshedTranchesLists={fetchRefreshedTranchesLists}
          sblcInitialFormData={sblcInitialFormData}
          sblcFormData={sblcFormData}
          files={files}
          uploadedFiles={uploadedFiles}
          toggleListData={togglePostShipmentListData}
          toggleDocumentList={toggleDocumentList}
          toggleFileList={toggleFileList}
          expandedFormSections={expandedFormSections}
          isLinkedThroughRoute={isLinkedThroughRoute}
          isVantiraDeal={Boolean(Number(selectedDeal?.is_vantira_deal))}
          setIsExistingShipmentDetails={setIsExistingPostShipment}
          setIsFormUpdated={setIsPostShipmentFormUpdated}
          setIsCurrenTrancheANewTranche={setIsCurrenTrancheANewTranche}
          isCurrenTrancheANewTranche={isCurrenTrancheANewTranche}
          setIsSBLCFormUpdated={setIsSBLCFormUpdated}
          setIsFormSavedOrDeleted={setIsPostShipmentFormSavedOrDeleted}
          setTranches={setTranches}
          setSelectedTranche={setSelectedTranche}
          setSelectedDealForAutopopulate={setSelectedDealForAutopopulate}
          setSelectedPFIToAutoPopulate={setSelectedPFIToAutoPopulate}
          setSelectedTrancheForAutoPopulate={setSelectedTrancheForAutoPopulate}
          setFormData={setPostShipmentFormData}
          setSBLCFormData={setSBLCFormData}
          setFiles={setFiles}
          setUploadedFiles={setUploadedFiles}
          setToggleListData={setTogglePostShipmentListData}
          setToggleDocumentList={setToggleDocumentList}
          setToggleFileList={setToggleFileList}
          setExpandedFormSections={setExpandedFormSections}
          setShowDeleteModal={setShowDeleteModal}
          setIsLoading={setIsLoading}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          handleChange={handleChange}
          logOut={logOut}
        />
      </Grid>

      <Grid size={{ xs: 12 }} className="mt-3">
        <Documentation
          ref={documentationRef}
          isWritable={isWritable}
          isDocumentWritable={isDocumentWritable}
          areDocumentsLoading={areDocumentsLoading}
          areFilesLoading={areFilesLoading}
          isExistingDocumentation={isExistingPostShipment}
          isFoodsOrPolymersDeal={isFoodsOrPolymersDeal}
          isSBLCDeal={isSBLCDeal}
          isUpdated={isUpdated}
          selectedBusinessUnit={selectedBusinessUnit}
          selectedPFI={selectedPFI?.id}
          selectedTranche={selectedTranche}
          isLinkedThroughRoute={isLinkedThroughRoute}
          isVantiraDeal={Boolean(Number(selectedDeal?.is_vantira_deal))}
          linkedPFI={linkedPFI}
          linkedTranche={linkedTranche}
          listData={postShipmentListData}
          documentPermissions={documentPermissions}
          documentRequestPermissions={documentRequestPermissions}
          formData={postShipmentFormData}
          lockedFields={lockedFields}
          files={files}
          banks={banks}
          uploadedFiles={uploadedFiles}
          toggleDocumentList={toggleDocumentList}
          toggleFileList={toggleFileList}
          expandedFormSections={expandedFormSections}
          setSelectedTranche={setSelectedTranche}
          setFormData={setPostShipmentFormData}
          commmercialInvoiceNo={commmercialInvoiceNo}
          setCommmercialInvoiceNo={setCommmercialInvoiceNo}
          setFiles={setFiles}
          requestedFiles={requestedFiles}
          setRequestedFiles={setRequestedFiles}
          setUploadedFiles={setUploadedFiles}
          setExpandedFormSections={setExpandedFormSections}
          setToggleDocumentList={setToggleDocumentList}
          setToggleFileList={setToggleFileList}
          setIsDocumentToBeGenerated={setIsDocumentToBeGenerated}
          setShowConfirmationDialog={setShowConfirmationDialog}
          setIsLoading={setIsLoading}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          logOut={logOut}
        />
      </Grid>

      <div className="d-flex gap-2 justify-content-end submit-buttons">
        <button
          type="button"
          disabled={isLoading || (!isWritable && !isDocumentWritable) || (!isWritable && uploadedFiles.length === 0)}
          className="btn btn-secondary custom-btn"
          onClick={() => ref.current?.handleSubmit()}
        >
          {isExistingPostShipment ? "Update" : "Save"}
        </button>

        {isExistingPostShipment && (() => {
          const disableReason = selectedTranche?.period_lock
            ? 'This tranche is locked and cannot be deleted.'
            : (!isWritable && !isDocumentWritable)
              ? 'Deletion is not allowed for this user.'
              : disableDeletion
                ? 'Deletion is not allowed for this user.'
                : isLoading
                  ? 'Please wait...'
                  : '';

          return (
            <Tooltip title={disableReason}>
              <span>
                <button
                  type="button"
                  disabled={isLoading || (!isWritable && !isDocumentWritable) || selectedTranche?.period_lock || disableDeletion}
                  className="btn custom-btn btn-danger"
                  style={{ pointerEvents: disableReason ? 'none' : 'auto' }}
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </button>
              </span>
            </Tooltip>
          );
        })()}
      </div>

      <Modal show={showSelectModal} onHide={handleCloseModal} centered>
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>
            Switching forms will discard any unsaved changes. Do you want to continue?
          </span>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <Button variant="outlined" size="small" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleConfirmTrancheSelection}>
            Proceed
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleCloseModal} centered className="delete-modal">
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>

            {isLinkedThroughRoute && linkedTranche?.id ?
              <>
                This will also delete the linked tranche data. Do you really want to delete?
              </>
              :
              <>
                Do you really want to delete the form details?
              </>
            }
            {" "}This process cannot be undone.
          </span>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <Button variant="outlined" size="small" onClick={handleCloseModal}>
            Cancel
          </Button>

          <Tooltip title={disableDeletion ? "Deletion is not allowed for this user" : ""}>
            <span>
              <Button
                variant="contained"
                size="small"
                disabled={disableDeletion}
                onClick={handleDelete}
                style={{ pointerEvents: disableDeletion ? "none" : "auto" }}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        </Modal.Footer>
      </Modal>
    </Grid>
  );
});

export default memo(PostShipment);